import EvmProvider, { type EvmProviderInterface } from '../provider';
import type { Abi, Address } from 'viem';
import { erc20Abi, parseUnits } from 'viem';
import { readContract, writeContract } from '@wagmi/core';
import {
    type EvmChainId,
    type EvmConsideration,
    EvmItemType,
    type EvmItemTypeAbi,
    type EvmMarketplace,
    EvmOrderType,
    type EvmOtherRecipientType
} from '@/service/EVM/types';
import type { ContractSettings, EvmOrderParameters } from '~/service/api/types';
import dayjs from 'dayjs';
import EvmSignature from '../signature';
import { NULL_ADDRESS, NULL_HASH } from '@/service/EVM/consts';
import { EvmOrderEIPSchema } from '@/service/EVM/EIP712Schemas';
import { isEvmErc1155, isEvmItemTypeAbi } from '@/service/utils';

export interface EvmListingInterface extends EvmProviderInterface {
    approveAbi: Record<EvmItemTypeAbi, Abi>;
}

export class EvmListingFactory extends EvmProvider implements EvmListingInterface {
    approveAbi;

    constructor({
        eventEmitter,
        config,
        approveAbi,
        chainMap
    }: EvmListingInterface) {
        super({
            eventEmitter,
            config,
            chainMap
        });
        this.approveAbi = approveAbi;
    }

    async buildOrder({
        userAddress,
       paramChainId,
       price,
       contract,
       identifier,
       marketContract,
       endTime,
       amount = 1,
       itemType,
       currencyItemType,
       currencyTokenAddress,
       otherRecipients
    }: {
        paramChainId: EvmChainId;
        userAddress: Address;
        price: number;
        marketplace: EvmMarketplace;
        contract: Address;
        identifier: string;
        endTime: string;
        amount?: number;
        marketContract: ContractSettings;
        currencyItemType: EvmItemType;
        currencyTokenAddress: Address;
        itemType: EvmItemType;
        otherRecipients: EvmOtherRecipientType[];
    }) {
        if (!userAddress) throw new Error('User is not connected');

        await this.approveContract({
            itemType,
            contract,
            marketplaceAddress: marketContract.address,
            userAddress
        });

        const order = await this.eventEmitter.emit('prepareOffer', async () =>
            await this.prepareOffer({
                userAddress,
                paramChainId,
                otherRecipients,
                currencyItemType,
                currencyTokenAddress,
                price,
                itemType,
                contract,
                identifier,
                endTime,
                amount
            }), { messenger: 'Prepare Offer is created' });

        const signatureService = new EvmSignature(order, EvmOrderEIPSchema);

        // function logBigIntObject(obj: Record<string, any>): void {
        //     console.log(
        //         JSON.stringify(obj, (key, value) =>
        //             typeof value === 'bigint' ? value.toString() : value, 2)
        //     );
        // }
        //
        // logBigIntObject(order);

        // console.log({
        //     name: marketContract.name,
        //     version: marketContract.version,
        //     // version: marketPlaceContract.version,
        //     chainId: paramChainId,
        //     verifyingContract: marketContract.address
        // }, 'То что подписываем');

        const domain = {
            name: marketContract.name
        };

        const signature = await signatureService.create(this.config, {
            domain: {
                name: marketContract.name,
                version: '1.6',
                chainId: paramChainId,
                verifyingContract: '0x158cB00a714DceA09b3A56340c8ae14ec35137F4'
            },
            primaryType: 'OrderComponents'
        });

        return { order, signature };
    }

    private async approveContract({ itemType, contract, userAddress, marketplaceAddress }: {
        itemType: EvmItemType;
        contract: Address;
        userAddress: Address;
        marketplaceAddress: Address;
    }) {
        if (!isEvmItemTypeAbi(itemType)) {
            throw new Error('Not supported itemType');
        }
        const abi = this.approveAbi[itemType];

        try {
            const approved = await this.eventEmitter.emit('approveContract', async () =>
                await readContract(this.config, {
                    address: contract,
                    abi,
                    functionName: 'isApprovedForAll',
                    args: [userAddress, marketplaceAddress]
                }), { isApprove: true });

            if (!approved) {
                return await writeContract(this.config, {
                    address: contract,
                    abi,
                    functionName: 'setApprovalForAll',
                    args: [marketplaceAddress, true]
                });
            }
        }
        catch (e) {
            throw new Error('Failed to approve contract');
        }
    }

    private async prepareOffer({
        userAddress,
                                   paramChainId,
                                   otherRecipients,
                                   price,
                                   itemType,
                                   contract,
                                   identifier,
                                   endTime,
                                   amount = 1,
                                   currencyItemType,
                                   currencyTokenAddress
    }: {
        paramChainId: EvmChainId;
        otherRecipients: EvmOtherRecipientType[];
        price: number;
        userAddress: Address;
        identifier: string;
        contract: Address;
        itemType: EvmItemType;
        currencyItemType: EvmItemType;
        currencyTokenAddress: Address;
        endTime: string;
        amount?: number;
    }): Promise<EvmOrderParameters> {
        const decimals = currencyItemType === EvmItemType.ERC20
            ? await readContract(this.config, {
                address: currencyTokenAddress,
                chainId: paramChainId,
                functionName: 'decimals',
                abi: erc20Abi
            }) as number
            : 18;

        const priceWei = parseUnits(price.toString(), decimals);

        const totalFeeWei = otherRecipients.reduce(
            (acc, item) => acc + (priceWei * BigInt(item.value)) / BigInt(100),
            BigInt(0)
        );

        const userReceivesWei = priceWei - totalFeeWei;

        const orderType = isEvmErc1155(itemType) && amount > 1 ? EvmOrderType.PARTIAL_OPEN : EvmOrderType.FULL_OPEN;

        const considerations: EvmConsideration[] = [
            ...(otherRecipients?.map((item) => {
                const feeWei = (priceWei * BigInt(item.value)) / BigInt(100);
                return {
                    token: item.token,
                    itemType: item.itemType,
                    identifierOrCriteria: 0,
                    startAmount: feeWei.toString(),
                    endAmount: feeWei.toString(),
                    recipient: item.address
                };
            }) ?? []),
            {
                token: currencyTokenAddress,
                itemType: currencyItemType,
                identifierOrCriteria: 0,
                startAmount: userReceivesWei.toString(),
                endAmount: userReceivesWei.toString(),
                recipient: userAddress
            }
        ];

        return {
            offerer: userAddress,
            offer: [
                {
                    itemType,
                    token: contract,
                    identifierOrCriteria: Number(identifier),
                    startAmount: amount,
                    endAmount: amount
                }
            ],
            consideration: considerations,
            startTime: Math.floor(Date.now() / 1000),
            endTime: dayjs(endTime).unix(),
            orderType,
            zone: NULL_ADDRESS,
            zoneHash: NULL_HASH,
            salt: this.generateSalt(),
            conduitKey: NULL_HASH,
            counter: 0,
            totalOriginalConsiderationItems: considerations.length
        };
    }
}
